import React, { memo } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { styled } from "@mui/system";
import { useMediaQuery } from "@mui/material";

import { theme } from "../../theme";
import { ICONS, Icon } from "../Icons";
import { getIsSendingProspect } from "../../store/selectors/sendProspectToAPI";

export const ArrowBack = styled(Icon)({
  width: "18px"
});

export const Root = styled(Link)({
  marginRight: "20px",
  display: "flex",
  alignItems: "center"
});

export const Text = styled("span")({
  fontSize: "14px",
  fontWeight: 600,
  textDecoration: "underline"
});

Text.defaultProps = {
  style: theme.palette.text
};

const StyledBackLink = styled(Link)(({ theme }) => ({
  minWidth: "unset",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "0.875rem",
  fontWeight: 600,
  textDecoration: "underline",
  color: "var(--Color-Text-Primary-On-Light, #1F1F1F)",
  "&.linkContainerBtn": {
    display: "flex",
    background: "#fff",
    border: "none",
    textDecoration: "none",
    gap: "5px",
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: "18px",
    fontStyle: "normal",

    [theme.breakpoints.up("sm")]: {
      padding: "0.75rem 2.96975rem 0.75rem 2.81738rem",
      alignItems: "center",
      gap: "0.5rem",
      borderRadius: "100px",
      border: "1px solid #3B3A3A",
      color: "#3B3A3A",
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: "1.75rem",
      height: "50px"
    },

    "& .arrowIcon": {
      fill: "#3B3A3A",
      "& path": {
        fill: "#3B3A3A"
      }
    },
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "var(--Color-Text-Primary-On-Light, #1F1F1F)",
      "& svg": {
        fill: "#ffffff",
        "& path": {
          fill: "#ffffff"
        }
      }
    },
    [theme.breakpoints.only("md")]: {
      fontSize: "1.125rem",
      padding: "12px 30px",
      lineHeight: "20px"
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.25rem",
      padding: "12px 45px"
    }
  },
  "& .arrowIcon": {
    fill: "#1F1F1F",
    "& path": {
      fill: "#1F1F1F"
    }
  }
}));

const BackLinkBase = ({ text = "Back", className = "", path, isTypeButton = false, ...props }) => {
  const isMobile = useMediaQuery("(max-width: 767px)") || window.innerWidth <= 768;
  const isProspectSaving = useSelector(getIsSendingProspect);

  const handleLinkClick = ev => {
    if (ev && isProspectSaving) {
      ev.preventDefault();
    }
    if (props.preventClick) {
      return;
    }
    props.onClick && props.onClick();
  };

  return (
    <div onClick={handleLinkClick} data-testid="back-link" className="back-link">
      <StyledBackLink
        className={cx(className, isTypeButton && "linkContainerBtn")}
        to={path}
        replace
        {...props}
      >
        <Icon className="arrowIcon" alt="collapse-icon" name={ICONS.arrowLeft} />
        <span>{isMobile ? "Go back" : text || "Back"}</span>
      </StyledBackLink>
    </div>
  );
};

/**
 * PropTypes for BackLink component
 */
BackLinkBase.propTypes = {
  /** Text to display on the back link */
  text: PropTypes.string,
  /** CSS class name for styling */
  className: PropTypes.string,
  /** Path to navigate to when clicked */
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Flag to render as button style */
  isTypeButton: PropTypes.bool,
  /** Function to handle click events */
  onClick: PropTypes.func,
  /** Flag to prevent click action */
  preventClick: PropTypes.bool
};

const areEqual = (prevProps, nextProps) =>
  prevProps.text === nextProps.text &&
  JSON.stringify(prevProps.path) === JSON.stringify(nextProps.path);

export const BackLink = memo(BackLinkBase, areEqual);
